import type { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/EmptyState';
import { cn } from '@/lib/utils';

export interface PanelProps {
  title: string;
  action?: ReactNode;
  /** True during the first load. */
  loading: boolean;
  error: string | null;
  /** True once data has been received (data !== undefined). */
  hasData: boolean;
  onRetry?: () => void;
  className?: string;
  /** Rendered when hasData is true. */
  children: ReactNode;
}

/** Dashboard card with a title row and loading / error / stale-data body states. */
export function Panel({ title, action, loading, error, hasData, onRetry, className, children }: PanelProps) {
  let body: ReactNode = null;
  if (hasData) {
    body = (
      <>
        {error !== null && (
          <div
            role="status"
            className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
          >
            <span>Showing last known data — refresh failed.</span>
            {onRetry && (
              <Button variant="ghost" size="xs" onClick={onRetry}>
                Retry
              </Button>
            )}
          </div>
        )}
        {children}
      </>
    );
  } else if (loading) {
    body = (
      <>
        <div aria-hidden className="space-y-3">
          <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        </div>
        <span className="sr-only" role="status">
          Loading {title}
        </span>
      </>
    );
  } else if (error !== null) {
    body = (
      <div role="status">
        <EmptyState
          as="h3"
          title={`Couldn't load ${title.toLowerCase()}`}
          description={error}
          className="border-0 py-6"
          action={
            onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                Retry
              </Button>
            )
          }
        />
      </div>
    );
  }

  return (
    <Card className={cn('min-w-0 gap-3 p-4', className)}>
      <div className="flex items-center justify-between gap-2">
        <h2 title={title} className="min-w-0 truncate text-base font-semibold">{title}</h2>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {body}
    </Card>
  );
}
